import type { UserResponse } from 'stream-chat';

import type User from '@/data/user/model';

export const getWorkchatUserShortName = (user: User): string => {
  return `${user.first_name} ${user.last_name.charAt(0)?.toUpperCase()}`;
};

export const getStreamUserFirstLastNames = (user: UserResponse): { firstName?: string; lastName?: string } => {
  const nameFragments = user.name?.split(' ');
  const firstName = nameFragments?.[0];
  const lastName = nameFragments?.[nameFragments.length - 1];

  return { firstName, lastName };
};

export const getStreamUserShortName = (user: UserResponse): string => {
  const { firstName, lastName } = getStreamUserFirstLastNames(user);

  if (firstName && lastName) {
    return `${firstName} ${lastName.charAt(0)?.toUpperCase()}`;
  } else if (firstName) {
    return firstName;
  } else if (lastName) {
    return lastName;
  }

  return '???';
};

export const parseEmojiFromUnicodeHex = (input: string) => {
  const hexCodes = input.split(' ').map(code => code.replace('U+', ''));
  return hexCodes.map(code => String.fromCodePoint(Number.parseInt(code, 16))).join('');
};

/**
 * Converts EmojiMart's Unified emoji code to Unicode Hex
 * @param input
 */
export const emojimartUnifiedToUnicodeHex = (input: string) => {
  return input
    ?.toUpperCase()
    .split('-')
    .map(part => `U+${part}`)
    .join(' ');
};

/**
 * Generates a channel ID in the format {accountId}-{channel-name-in-kebab-case}
 * @param accountId - The account ID number
 * @param channelName - The channel name to convert to kebab-case
 * @returns The formatted channel ID
 */
export const generateStreamChannelId = (accountId: number, channelName: string): string => {
  const kebabName = channelName
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens

  return `${accountId}-${kebabName}`;
};
